import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  Loading,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider, BlogCategory } from '../dataProvider';

/**
 * Blog-category edit (D-cms-blog). The label and description are editable; the KEY is
 * IMMUTABLE and shown read-only, because a post references the key (changing it would
 * orphan the post's category). There is NO delete. Saving calls
 * `dataProvider.update('blogCategories')`; the #25 wrapper records the diff. Gated
 * cms:author; a viewer is denied at the route.
 */

interface BlogCategoryEditValues {
  label: string;
  description: string;
}

const BlogCategoryEditForm = ({ record }: { record: BlogCategory }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: BlogCategoryEditValues = {
    label: record.label,
    description: record.description,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as BlogCategoryEditValues;
    const data = { label: values.label, description: values.description };
    const previousData = {
      label: record.label,
      description: record.description,
    };
    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('blogCategories', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('Category updated', { type: 'success' });
      redirect('list', 'blogCategories');
    } catch {
      notify('Could not save the category', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.label}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            {/* The key is the stable category ref a post points at — read-only here. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Key (set at creation, immutable)
              </Typography>
              <Typography variant="body1" data-testid="blog-category-key">
                {record.key}
              </Typography>
            </Box>
            <TextInput
              source="label"
              label="Label"
              validate={required('Label is required')}
              fullWidth
            />
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('list', 'blogCategories')}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const BlogCategoryEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<BlogCategory>(
    'blogCategories',
    {
      id: Number(id),
    },
  );
  if (isLoading || !record) return <Loading />;
  return <BlogCategoryEditForm record={record} />;
};

/** Gated entry point registered as the `blogCategories` Resource's edit. */
export const BlogCategoryEdit = () => (
  <RequireAccess capability="cms:author">
    <BlogCategoryEditView />
  </RequireAccess>
);
